import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import { AxiosError } from 'axios';
import useHandleApiError from '../../../hooks/useHandleApiError';
import { AppExceptionResponseData } from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { queryClient } from '../../../modules/reactQuery/contexts/QueryClient/utils/queryClient';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import { formatUserContextFiltersToSave } from './userContextPreference.utils';
import { getFindUserContextPreferencesKey } from './userContextPreferences.keys';
import {
  updateOrCreateColumnsPreference,
  updateOrCreateFilterPreference,
  updateOrCreateUiPreference,
  updateOrCreateViewPreference,
} from './userContextPreferences.repository';
import {
  UiPreferenceRecognizedKeys,
  UpdateOrCreateColumnsPreferenceInput,
  UpdateOrCreateFilterPreferenceInput,
  UpdateOrCreateUiPreferenceInput,
  UpdateOrCreateViewPreferenceInput,
  UserContextPreferences,
} from './userContextPreferences.types';

export const useUpdateOrCreateFilterPreference = () => {
  const { team } = useApplicationContext();

  return useMutation({
    mutationFn: async (input: UpdateOrCreateFilterPreferenceInput) => {
      const formateUserContextFilters = formatUserContextFiltersToSave(input, team?.id);
      const res = await updateOrCreateFilterPreference(formateUserContextFilters);
      if (!res.ok) {
        throw new Error(
          `erro criando ou atualizando filter preference para lista ${formateUserContextFilters.list}`,
        );
      }
      return res.data;
    },
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: [team?.id, ...getFindUserContextPreferencesKey()],
      });
    },
  });
};

export const useUpdateOrCreateColumnsPreference = () => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const optimisticValue = queryClient.getQueryData([
    team?.id,
    ...getFindUserContextPreferencesKey(),
  ]);

  const updateQueryData = (input: UpdateOrCreateColumnsPreferenceInput) => {
    queryClient.setQueryData(
      [team?.id, ...getFindUserContextPreferencesKey()],
      (oldData: UserContextPreferences | undefined): UserContextPreferences | undefined => {
        if (oldData) {
          return {
            ...oldData,
            columnsPreferences: (oldData?.columnsPreferences || [])?.map((preference) => {
              if (preference.list === input.list) {
                return input;
              }
              return preference;
            }),
          };
        }
      },
    );
  };

  const mutation = useMutation({
    mutationFn: async (input: UpdateOrCreateColumnsPreferenceInput) => {
      const res = await updateOrCreateColumnsPreference(input);
      if (!res.ok) {
        throw new Error(`erro criando ou atualizando filter preference para lista ${input.list}`);
      }
      return res.data;
    },
    onMutate: async (input: UpdateOrCreateColumnsPreferenceInput) => {
      const previousData: ApiResponse<UserContextPreferences> | undefined =
        queryClient.getQueryData([team?.id, ...getFindUserContextPreferencesKey()]);

      updateQueryData(input);
      return { previousData };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [team?.id, ...getFindUserContextPreferencesKey()],
      });
    },
  });

  return { ...mutation, optimisticValue };
};

export const useUpdateOrCreateUiPreference = () => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const queryKey = [team?.id, ...getFindUserContextPreferencesKey()];

  const updateQueryData = ({ key, value }: UpdateOrCreateUiPreferenceInput) => {
    queryClient.setQueryData(
      queryKey,
      (
        oldData: UserContextPreferences | null | undefined,
      ): UserContextPreferences | null | undefined => {
        if (!oldData) {
          return oldData;
        }

        const uiPreferences = oldData.uiPreferences ?? [];
        const hasPreference = uiPreferences.some((preference) => preference.key === key);

        return {
          ...oldData,
          uiPreferences: hasPreference
            ? uiPreferences.map((preference) =>
                preference.key === key ? { key, value } : preference,
              )
            : [...uiPreferences, { key, value }],
        };
      },
    );
  };

  return useMutation({
    mutationFn: async (input: UpdateOrCreateUiPreferenceInput) => {
      const res = await updateOrCreateUiPreference(input);
      if (!res.ok) {
        throw new Error(`failed to create or update the ui preference for the key ${input.key}`);
      }
      return res.data;
    },
    onMutate: async (input: UpdateOrCreateUiPreferenceInput) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData: UserContextPreferences | null | undefined =
        queryClient.getQueryData(queryKey);

      updateQueryData(input);

      return { previousData };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(queryKey, context?.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdateOrCreateViewPreference = () => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  const preferencesKey = [team?.id, ...getFindUserContextPreferencesKey()];

  const applyOptimistic = ({ defaultView }: UpdateOrCreateViewPreferenceInput) => {
    queryClient.setQueryData(
      preferencesKey,
      (
        oldData: UserContextPreferences | null | undefined,
      ): UserContextPreferences | null | undefined => {
        if (!oldData) {
          return oldData;
        }

        const otherPreferences = (oldData.uiPreferences ?? []).filter(
          (preference) => preference.key !== UiPreferenceRecognizedKeys.CATALOG_VIEW,
        );

        return {
          ...oldData,
          uiPreferences:
            defaultView === null
              ? otherPreferences
              : [
                  ...otherPreferences,
                  { key: UiPreferenceRecognizedKeys.CATALOG_VIEW, value: { ...defaultView } },
                ],
        };
      },
    );
  };

  return useMutation({
    mutationFn: async (input: UpdateOrCreateViewPreferenceInput) => {
      const res = await updateOrCreateViewPreference(input);

      if (!res.ok) {
        throw res.originalError;
      }

      return res.data;
    },
    onMutate: async (input: UpdateOrCreateViewPreferenceInput) => {
      await queryClient.cancelQueries({ queryKey: preferencesKey });
      const previousData = queryClient.getQueryData<UserContextPreferences>(preferencesKey);

      applyOptimistic(input);

      return { previousData };
    },
    onError: (error, _input, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(preferencesKey, context.previousData);
      }

      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.GLOBAL_ERROR_MESSAGE)),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: preferencesKey });
    },
  });
};
